export const WALLET_CANCEL_MESSAGE_LIMIT = 200;

const FAILED_WALLET_STATUS = new Set(['failed', 'cancelled', 'canceled', 'requires_payment_method']);

const AUTHORIZED_WALLET_STATUS = new Set(['requires_capture', 'succeeded', 'processing']);

const WALLET_CLOSE_EVENTS: Record<string, 'googlepay_syscheeckout_closed' | 'applepay_syscheeckout_closed'> = {
  google_pay: 'googlepay_syscheeckout_closed',
  apple_pay: 'applepay_syscheeckout_closed',
};

export function readWalletPaymentId(payload: unknown): string | null {
  const data = (payload ?? {}) as Record<string, unknown>;
  const id = [data.payment_id, data.paymentId].find(value => typeof value === 'string' && value);

  return (id as string | undefined) ?? null;
}

export function readWalletFailure(payload: unknown): string | null {
  const data = (payload ?? {}) as Record<string, unknown>;
  const error = (data.error ?? {}) as Record<string, unknown>;
  const status = typeof data.status === 'string' ? data.status : '';

  if (AUTHORIZED_WALLET_STATUS.has(status)) return null;

  const message = [data.error_message, error.message, data.message]
    .find(value => typeof value === 'string' && value) as string | undefined;
  const code = [data.error_code, error.code, error.decline_code]
    .find(value => typeof value === 'string' && value) as string | undefined;

  if (!message && !code && !FAILED_WALLET_STATUS.has(status)) return null;

  return `${message ?? 'Wallet payment failed'} code:${code ?? status ?? 'wallet_failed'}`;
}

export function walletCloseEvent(
  paymentMethodType: string | null,
): 'googlepay_syscheeckout_closed' | 'applepay_syscheeckout_closed' | null {
  return paymentMethodType ? WALLET_CLOSE_EVENTS[paymentMethodType] ?? null : null;
}

interface WalletCancelPayload {
  status?: unknown;
  statusCode?: unknown;
  errorCode?: unknown;
  error?: unknown;
  message?: unknown;
}

const USER_CANCEL_PATTERN = /cancel|abort|dismiss|closed_by_user/i;
const ERROR_PATTERN = /error|fail|declin|reject|unavailable/i;

function readText(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  return typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
}

function readNested(payload: unknown): WalletCancelPayload {
  const data = (payload ?? {}) as WalletCancelPayload;

  return (typeof data.error === 'object' && data.error ? data.error : {}) as WalletCancelPayload;
}

function readMessage(payload: unknown): string {
  const data = (payload ?? {}) as WalletCancelPayload;

  return readText(data.message) || readText(readNested(payload).message);
}

function collectSignals(payload: unknown): string[] {
  const data = (payload ?? {}) as WalletCancelPayload;
  const nested = readNested(payload);

  return [
    readText(data.status),
    readText(data.statusCode),
    readText(data.errorCode),
    readText(data.message),
    readText(data.error),
    readText(nested.status),
    readText(nested.statusCode),
    readText(nested.errorCode),
    readText(nested.message),
  ].filter(Boolean);
}

export function resolveWalletCancelReason(payload: unknown): 'user' | 'error' | 'unknown' {
  const data = (payload ?? {}) as WalletCancelPayload;
  const signals = collectSignals(payload);

  if (signals.some(signal => USER_CANCEL_PATTERN.test(signal))) {
    return 'user';
  }

  const hasErrorField = Boolean(data.error || data.errorCode || readNested(payload).errorCode);

  if (hasErrorField || readMessage(payload) || signals.some(signal => ERROR_PATTERN.test(signal))) {
    return 'error';
  }

  return 'unknown';
}

export function extractWalletCancelMessage(payload: unknown): string {
  const message = readMessage(payload)
    || collectSignals(payload).find(signal => !USER_CANCEL_PATTERN.test(signal))
    || 'wallet closed without payment';

  return message.slice(0, WALLET_CANCEL_MESSAGE_LIMIT);
}
